import { apiUrl } from "@/lib/api";
import { trackEvent } from "./tracker";

/* Reading a resume, without anybody watching it happen.

   Turning a resume into three postings and a profile is a model call over a
   whole document: tens of seconds, sometimes over a minute. The old signup
   awaited it inline behind a progress bar, so the very first thing a person
   did on Sift was sit still and wait. Uploading a resume is the single most
   valuable thing an individual can hand us, and we were charging a minute of
   dead time for it.

   Nothing about that work needs the person present. So this module owns the
   request instead of a component: signup fires it and moves straight on to the
   account form, and the finish-setup screen picks the same promise back up
   afterwards. The reading happens while they type their email.

   Module scope, not React state, because the whole point is that it outlives
   the screen that started it. A client-side route change does not touch a
   fetch already in flight; unmounting the component that started it does not
   either. What DOES kill it is a real document reload, which is a normal event
   in the in-app browsers most of this traffic uses — so every result is also
   written to sessionStorage as it lands, and the finish screen falls back to
   asking the server to re-read the copy now stored on their profile.

   Deliberately not a React context: the two screens that care are on opposite
   sides of a navigation and a login, and a provider spanning both would have
   to wrap the entire app to hold one file. */

// Where a landed result waits out a reload between signup and finish-setup.
const RESULT_KEY = "sift_resume_intake_result";

// The live job. `promise` always resolves — never rejects — to the payload
// shape below, so no caller has to wrap it and no unhandled rejection can
// escape while nobody is listening.
let job = null;

/** The normalized shape every caller sees, whatever went wrong underneath. */
const failure = (reason) => ({ available: false, reason, postings: [], profile: null });

function persist(result) {
  try {
    sessionStorage.setItem(RESULT_KEY, JSON.stringify({ ...result, at: Date.now() }));
  } catch {
    /* private mode; the in-memory promise is still the primary path */
  }

  /* The outcome has to be recorded HERE, not by whoever started the job.

     The resume read is deliberately not awaited -- the person goes on to the
     account form while it runs -- so the screen that kicked it off has moved on
     long before there is anything to report. The result was that
     need_draft_requested fired 157 times over a fortnight and need_draft_ready
     fired ONCE, and the Growth Funnel's "Got a drafted post" row read as a dead
     step while 125 of those 157 people went on to create accounts.

     Fired once per job, from the one place every path converges on. */
  try {
    if (result?.available && (result.postings || []).length) {
      trackEvent("need_draft_ready", {
        source: "resume", flow: "need_first", count: result.postings.length,
      });
    } else {
      trackEvent("need_draft_failed", {
        source: "resume", flow: "need_first", reason: result?.reason || "could_not_read",
      });
    }
  } catch {
    /* Reporting must never break the read it is reporting on. */
  }
}

/**
 * Start reading a resume. Returns immediately — the returned promise is for
 * whoever wants to wait, and nobody has to.
 *
 * Calling it again replaces the previous job: picking a second file means the
 * first one is no longer what they meant to send.
 */
export function startResumeIntake(file, { anonymousId } = {}) {
  clearResumeIntake();
  const startedAt = Date.now();

  /* Two calls at once, not one.

     The single call writes three postings and a profile and takes about twelve
     seconds, which is longer than the account form takes to fill in, so the
     person never saw what we wrote until after signup. "first" asks for the
     strongest posting alone and lands in about four, in time for the signup
     screen to show it. "rest" writes the other two and the profile, and is
     only needed on the finish-setup screen after login. */
  const first = readPart(file, anonymousId, "first").then((result) => {
    trackEvent("resume_first_post", {
      flow: "need_first", ok: !!result.available, ms: Date.now() - startedAt,
      reason: result.available ? "" : result.reason,
    });
    return result;
  });
  const rest = readPart(file, anonymousId, "rest");

  const promise = Promise.all([first, rest])
    .then(([a, b]) => {
      const result = mergeParts(a, b);
      persist(result);
      return result;
    })
    .finally(() => {
      if (job) job.settled = true;
    });

  job = { file, promise, first, settled: false, startedAt };
  return promise;
}

/** One half of the read. Always resolves, to the normalized shape. */
function readPart(file, anonymousId, part) {
  const form = new FormData();
  form.append("resume", file);
  form.append("part", part);
  if (anonymousId) form.append("anonymous_id", anonymousId);

  return fetch(apiUrl("/api/accounts/public/resume/suggest-postings/"), {
    method: "POST",
    body: form,
    credentials: "omit",
    mode: "cors",
  })
    .then(async (res) => {
      const data = res.ok ? await res.json().catch(() => ({})) : {};
      const postings = Array.isArray(data?.postings) ? data.postings.filter((p) => p?.title) : [];
      return data?.available && postings.length
        ? { available: true, reason: "", postings, profile: data.profile || null }
        : { ...failure(data?.reason || "could_not_read"), profile: data?.profile || null };
    })
    // The request never landed, so the file itself is unjudged. Say that
    // rather than implying there was something wrong with their resume.
    .catch(() => failure("upload_failed"));
}

/* The two halves as the one result finish-setup has always read.

   The first post leads, because it is the one they already saw on the signup
   screen. Either half can fail on its own and the other still counts: one good
   posting beats none. The profile comes from "rest", which is the half asked to
   write it; "first" only carries the no-model fallback the server builds from
   the text. A "rest" posting with the same title as the first is dropped, since
   the two calls cannot see each other's answers. */
function mergeParts(a, b) {
  const lead = a.available ? a.postings.slice(0, 1) : [];
  const taken = new Set(lead.map((p) => p.title.trim().toLowerCase()));
  const others = b.available
    ? b.postings.filter((p) => !taken.has(p.title.trim().toLowerCase()))
    : [];
  const postings = [...lead, ...others];
  const profile = b.profile || a.profile || null;
  if (postings.length) return { available: true, reason: "", postings, profile };
  return { ...failure(b.reason || a.reason || "could_not_read"), profile };
}

/** The first post on its own, for the signup screen's preview. Resolves to the
 *  normalized shape, or null when there is no read in flight. */
export function awaitFirstPost() {
  if (job?.first) return job.first;
  const landed = peekResumeResult();
  return Promise.resolve(landed || null);
}

/**
 * The fast half: the name and city off the top of the page, with no model call
 * behind it.
 *
 * Never aborts, and takes no timeout. Callers that cannot wait should race it
 * against `afterMs` below and keep hold of this promise for the late answer.
 *
 * It used to abort itself at two seconds, which was the wrong lever. Measured
 * against production the round trip runs right at that line (1.5s to the API by
 * curl, more from a browser once the CORS preflight is counted), so the cap was
 * firing on perfectly good requests and throwing away the answer they were
 * about to give. The smoke test caught it: the account form arrived in 2.9s,
 * exactly as intended, with an empty name box.
 *
 * Not waiting and cancelling are different things. We stop waiting; the request
 * carries on, and the name drops into the form a moment later.
 */
export async function quickReadResume(file, { anonymousId } = {}) {
  const form = new FormData();
  form.append("resume", file);
  if (anonymousId) form.append("anonymous_id", anonymousId);

  try {
    const res = await fetch(apiUrl("/api/accounts/public/resume/quick-read/"), {
      method: "POST",
      body: form,
      credentials: "omit",
      mode: "cors",
    });
    if (!res.ok) return null;
    return await res.json().catch(() => null);
  } catch {
    return null; // offline, blocked, CORS — all the same to the caller
  }
}

/**
 * Resolve to `promise`'s value if it lands within `ms`, otherwise to null.
 * The promise is left running either way, so a slow answer is late rather
 * than lost.
 */
export function withinMs(promise, ms) {
  let timer;
  const deadline = new Promise((resolve) => { timer = setTimeout(() => resolve(null), ms); });
  return Promise.race([promise.then((v) => { clearTimeout(timer); return v; }), deadline]);
}

/** The live job, or null. `file` is here so signup can upload the real file to
 *  the profile once an account exists to attach it to. */
export function getResumeIntake() {
  return job;
}

export function hasResumeIntake() {
  return !!job || !!peekResumeResult();
}

/** A landed result recovered from storage after a reload. Non-destructive. */
export function peekResumeResult() {
  try {
    const raw = sessionStorage.getItem(RESULT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Wait for the reading to finish, however it started.
 *
 * Prefers the live promise and falls back to a result that landed before a
 * reload. Returns null when there is no intake at all, which is a different
 * answer from a failed one: nothing was ever uploaded.
 */
export async function awaitResumeIntake() {
  if (job) return job.promise;
  return peekResumeResult();
}

export function clearResumeIntake() {
  job = null;
  try {
    sessionStorage.removeItem(RESULT_KEY);
  } catch {
    /* nothing to clear */
  }
}
